"use client";

import { useEffect, useState } from "react";

type Review = {
    rating: number;
    title: string | null;
    body: string;
    authorName: string;
    createdAt: string;
};

type ReviewListResponse = {
    items: Review[];
};

type ReviewListBlockProps = {
    apiBasePath: string;
    siteUuid: string;
    pageUuid?: string | null;
    title: string;
};

function reviewPath(apiBasePath: string, siteUuid: string, pageUuid?: string | null): string {
    const base = `${apiBasePath}/sites/${siteUuid}`;

    return pageUuid ? `${base}/pages/${pageUuid}/reviews` : `${base}/reviews`;
}

function reviewKey(review: Review, index: number): string {
    return `${review.createdAt}:${review.authorName}:${review.rating}:${review.title ?? ""}:${index}`;
}

export function ReviewListBlock({ apiBasePath, siteUuid, pageUuid, title }: ReviewListBlockProps) {
    const [reviews, setReviews] = useState<Review[]>([]);

    useEffect(() => {
        let cancelled = false;

        void fetch(reviewPath(apiBasePath, siteUuid, pageUuid), { credentials: "include" })
            .then((response) => response.ok ? response.json() as Promise<ReviewListResponse> : null)
            .then((payload) => {
                if (!cancelled) {
                    setReviews(payload?.items ?? []);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setReviews([]);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [apiBasePath, siteUuid, pageUuid]);

    if (reviews.length === 0) {
        return null;
    }

    return (
        <section className="not-prose my-10">
            <h2 className="font-[var(--site-heading-font)] text-3xl font-bold text-[var(--site-text)]">{title}</h2>
            <div className="mt-5 grid gap-4 md:grid-cols-3">
                {reviews.map((review, reviewIndex) => (
                    <article key={reviewKey(review, reviewIndex)} className="rounded-[var(--site-radius)] border border-[var(--site-border)] bg-[var(--site-surface)] p-5">
                        <p className="text-sm font-black text-amber-600">{"★".repeat(Math.max(1, Math.min(5, review.rating)))}</p>
                        {review.title ? <h3 className="mt-3 font-[var(--site-heading-font)] text-lg font-bold text-[var(--site-text)]">{review.title}</h3> : null}
                        <p className="mt-2 text-sm leading-6 text-[var(--site-muted)]">{review.body}</p>
                        <p className="mt-4 text-sm font-semibold text-[var(--site-text)]">{review.authorName}</p>
                    </article>
                ))}
            </div>
        </section>
    );
}
